import { AdPlatform, CampaignObjective, CreativeType } from '@/shared/types/enums';

/**
 * Platform-specific creative constraints for headlines, descriptions, and images.
 */
export interface CreativeFieldConstraints {
  min: number;
  max: number;
  maxLength: number;
}

export interface PlatformCreativeConstraints {
  headlines: CreativeFieldConstraints;
  descriptions: CreativeFieldConstraints;
  images: { min: number; max: number; sizes: string[] };
}

export const PLATFORM_CREATIVE_CONSTRAINTS: Record<AdPlatform, PlatformCreativeConstraints> = {
  [AdPlatform.META]: {
    headlines: { min: 1, max: 5, maxLength: 40 },
    descriptions: { min: 1, max: 5, maxLength: 125 },
    images: { min: 1, max: 10, sizes: ['1200x628', '1080x1080'] },
  },
  [AdPlatform.GOOGLE]: {
    headlines: { min: 3, max: 15, maxLength: 30 },
    descriptions: { min: 2, max: 4, maxLength: 90 },
    // Responsive Display Ads pool up to 15 marketing images per aspect ratio.
    images: { min: 1, max: 15, sizes: ['various'] },
  },
  [AdPlatform.TWITTER]: {
    headlines: { min: 1, max: 1, maxLength: 70 },
    descriptions: { min: 1, max: 1, maxLength: 280 },
    images: { min: 1, max: 4, sizes: ['800x418', '800x800'] },
  },
  // TikTok M1 ships connect-only — wizard cannot reach this branch yet.
  // M2 will replace these with the real 9:16-video constraints.
  [AdPlatform.TIKTOK]: {
    headlines: { min: 1, max: 1, maxLength: 100 },
    descriptions: { min: 1, max: 1, maxLength: 100 },
    images: { min: 1, max: 1, sizes: ['1080x1920'] },
  },
};

/**
 * Which targeting features each platform actually supports at dispatch.
 * Components gate sections on these flags instead of `platform ===`
 * conditionals — add a flag here when a new platform difference appears.
 */
export interface PlatformTargetingCapabilities {
  /** Keyword targeting exists on the platform (Google search terms, X timeline keywords). Meta has no keyword concept. */
  keywords: boolean;
  /** Meta behavior targeting (targeting spec `behaviors`). */
  behaviors: boolean;
}

export const PLATFORM_TARGETING_CAPABILITIES: Record<AdPlatform, PlatformTargetingCapabilities> = {
  [AdPlatform.META]: { keywords: false, behaviors: true },
  [AdPlatform.GOOGLE]: { keywords: true, behaviors: false },
  [AdPlatform.TWITTER]: { keywords: true, behaviors: false },
  [AdPlatform.TIKTOK]: { keywords: false, behaviors: false },
};

/** Safe accessor for contexts where platform may be unset (mid-wizard drafts). */
export function getTargetingCapabilities(platform?: AdPlatform): PlatformTargetingCapabilities {
  return platform
    ? PLATFORM_TARGETING_CAPABILITIES[platform]
    : { keywords: true, behaviors: false };
}

/**
 * Platform-specific objective availability.
 */
export const PLATFORM_OBJECTIVES: Record<AdPlatform, CampaignObjective[]> = {
  [AdPlatform.META]: [
    CampaignObjective.AWARENESS,
    CampaignObjective.TRAFFIC,
    CampaignObjective.CONVERSIONS,
    CampaignObjective.TICKET_SALES,
  ],
  [AdPlatform.GOOGLE]: [
    CampaignObjective.AWARENESS,
    CampaignObjective.TRAFFIC,
    CampaignObjective.CONVERSIONS,
    CampaignObjective.TICKET_SALES,
  ],
  [AdPlatform.TWITTER]: [
    CampaignObjective.AWARENESS,
    CampaignObjective.TRAFFIC,
    CampaignObjective.CONVERSIONS,
  ],
  // M1 connect-only — wizard never reaches TikTok yet.
  [AdPlatform.TIKTOK]: [CampaignObjective.TRAFFIC],
};

/**
 * Platform-specific help text for the creative step.
 */
export const PLATFORM_CREATIVE_HELP: Record<AdPlatform, string> = {
  [AdPlatform.META]:
    'Meta supports 1-5 headlines (max 40 chars), 1-5 descriptions (max 125 chars) and up to 10 images. Meta mixes your images and texts automatically and shows the best combinations.',
  [AdPlatform.GOOGLE]:
    'Google requires 3-15 headlines (max 30 chars each) and 2-4 descriptions (max 90 chars each), and accepts up to 15 images. Google mixes your assets automatically and shows the best combinations.',
  [AdPlatform.TWITTER]:
    'X (Twitter) allows 1 headline (max 70 chars) and 1 description (max 280 chars).',
  // M2 will replace with the real TikTok creative guidance.
  [AdPlatform.TIKTOK]:
    'TikTok requires a 9:16 vertical video (1080x1920) and a single short caption.',
};

/**
 * Platform-specific notes for objectives.
 */
export const PLATFORM_OBJECTIVE_NOTES: Partial<
  Record<AdPlatform, Partial<Record<CampaignObjective, string>>>
> = {
  [AdPlatform.GOOGLE]: {
    [CampaignObjective.AWARENESS]: 'Maps to Display campaigns on Google Ads.',
  },
};

/**
 * Validates field values against platform constraints.
 * Returns an error message or null if valid.
 */
export function validateFieldCount(
  fieldName: string,
  values: string[],
  constraints: CreativeFieldConstraints,
): string | null {
  const nonEmpty = values.filter(Boolean);
  if (nonEmpty.length < constraints.min) {
    return `At least ${constraints.min} ${fieldName}${constraints.min > 1 ? 's' : ''} required.`;
  }
  if (values.length > constraints.max) {
    return `Maximum ${constraints.max} ${fieldName}${constraints.max > 1 ? 's' : ''} allowed.`;
  }
  return null;
}

/**
 * Minimal structural shape needed to validate a creative's media. Kept narrow
 * so both the wizard form values and the persisted WizardCreative satisfy it.
 */
export interface CreativeMediaInput {
  type: CreativeType;
  generatedImages?: { imageStorageKey?: string | null }[];
  /** Square (1:1) slot — Google Display requires this alongside the landscape. */
  squareGeneratedImages?: { imageStorageKey?: string | null }[];
  /** Video slot — VIDEO creatives must have an uploaded video. */
  videoStorageKey?: string | null;
  /** Cover image — VIDEO creatives require a thumbnail (Meta rejects without). */
  videoThumbnailStorageKey?: string | null;
  /** YouTube URL — Google VIDEO creatives reference a YouTube video by URL. */
  youtubeVideoUrl?: string | null;
  /** Carousel cards — CAROUSEL creatives need 2-10, each with an uploaded image. */
  carouselCards?: { imageStorageKey?: string | null }[];
}

/** Meta carousel card count bounds, mirrored from the backend. */
export const CAROUSEL_MIN_CARDS = 2;
export const CAROUSEL_MAX_CARDS = 10;

function hasUploadedImage(images: { imageStorageKey?: string | null }[] | undefined): boolean {
  return (images ?? []).some((img) => !!img.imageStorageKey);
}

/**
 * True only when the creative has a (landscape) image whose upload actually
 * completed.
 *
 * `generatedImages` is the source of truth: it holds the persisted storage key
 * (from a manual upload's onSuccess or AI generation) and is the only field the
 * launch payload forwards to the backend. `mediaPreviews` can show a local blob
 * preview even when the S3 upload silently failed, so it must NOT count here.
 */
export function creativeHasImage(creative: CreativeMediaInput): boolean {
  return hasUploadedImage(creative.generatedImages);
}

/**
 * Validates that creatives carry the image their type/placement requires.
 *
 * - IMAGE/CAROUSEL creatives must have at least one uploaded image.
 * - Google AWARENESS maps to a Display campaign, which needs BOTH a landscape
 *   (1.91:1) image AND a square (1:1) image.
 *
 * Returns human-readable error messages (empty array = valid).
 */
export function validateCreativeMedia(
  creatives: CreativeMediaInput[],
  platform: AdPlatform | undefined,
  objective: CampaignObjective | undefined,
): string[] {
  const errors: string[] = [];
  const prefix = (i: number) => (creatives.length > 1 ? `Creative ${i + 1}: ` : '');
  const isGoogleDisplay =
    platform === AdPlatform.GOOGLE && objective === CampaignObjective.AWARENESS;

  let hasImageTypeCreative = false;
  creatives.forEach((creative, i) => {
    // VIDEO creatives differ by platform. Google references a YouTube-hosted
    // video by URL (no byte upload, no thumbnail). Everyone else (Meta) needs an
    // uploaded video AND a cover image — Meta rejects a video ad without one.
    if (creative.type === CreativeType.VIDEO) {
      if (platform === AdPlatform.GOOGLE) {
        if (!creative.youtubeVideoUrl) {
          errors.push(`${prefix(i)}add a YouTube URL for the video before continuing.`);
        }
        return;
      }
      if (!creative.videoStorageKey) {
        errors.push(
          `${prefix(i)}add a video before continuing — the preview alone doesn't count, the upload must finish.`,
        );
      }
      if (!creative.videoThumbnailStorageKey) {
        errors.push(`${prefix(i)}add a cover image for the video — it's required to launch.`);
      }
      return;
    }
    // CAROUSEL creatives need 2-10 cards, each with an uploaded image.
    if (creative.type === CreativeType.CAROUSEL) {
      const cards = creative.carouselCards ?? [];
      if (cards.length < CAROUSEL_MIN_CARDS) {
        errors.push(
          `${prefix(i)}add at least ${CAROUSEL_MIN_CARDS} carousel cards before continuing.`,
        );
      } else if (cards.length > CAROUSEL_MAX_CARDS) {
        errors.push(`${prefix(i)}carousel ads allow at most ${CAROUSEL_MAX_CARDS} cards.`);
      }
      if (cards.some((card) => !card.imageStorageKey)) {
        errors.push(
          `${prefix(i)}every carousel card needs an image — the upload must finish before continuing.`,
        );
      }
      return;
    }
    const needsImage = creative.type === CreativeType.IMAGE;
    if (!needsImage) return;
    hasImageTypeCreative = true;
    if (!creativeHasImage(creative)) {
      errors.push(
        `${prefix(i)}add a${isGoogleDisplay ? ' landscape (1.91:1)' : 'n'} image before continuing — the preview alone doesn't count, the upload must finish.`,
      );
    }
    // Google Display also requires a square (1:1) image alongside the landscape.
    if (isGoogleDisplay && !hasUploadedImage(creative.squareGeneratedImages)) {
      errors.push(
        `${prefix(i)}add a square (1:1) image — Google Display needs both a landscape and a square image.`,
      );
    }
  });

  // Display campaigns still need images even when the user picked a non-image
  // creative type. Only emit when no IMAGE/CAROUSEL creative already surfaced a
  // per-creative error above, to avoid a duplicate message.
  if (
    isGoogleDisplay &&
    !hasImageTypeCreative &&
    !(
      creatives.some(creativeHasImage) &&
      creatives.some((c) => hasUploadedImage(c.squareGeneratedImages))
    )
  ) {
    errors.push(
      'Google awareness campaigns run on Display and need a landscape (1.91:1) and a square (1:1) image. Add both before launching.',
    );
  }

  return errors;
}
